import { Box, forwardRef, Image, StyleProps, useColorModeValue } from '@chakra-ui/react';
import { PropsWithChildren, ReactNode } from 'react';

import { createUseVariantStyle } from '@bonusly/ds-hooks';
import { rem } from '@bonusly/ds-utils';

import { useSeasonalAvatarOverlay } from '../../../components/seasonal/use-seasonal-avatar-overlay';
import { useNameStyle } from '../hooks/use-name-style';
import { AvatarContextProvider } from '../lib/context';
import { AvatarSize, AvatarVariant } from '../types';
import { AvatarImage } from './avatar-image';
import { AvatarImageWrapper } from './avatar-image-wrapper';

export interface AvatarProps extends StyleProps, PropsWithChildren {
	name?: string;
	size?: AvatarSize;
	src?: string;
	variant?: AvatarVariant;
	/**
	 * Optional overlay content rendered on top of the avatar.
	 * Useful for badges, wreaths, status indicators, or other decorations.
	 * The overlay is positioned absolutely and centered on the avatar.
	 *
	 * @example
	 * <Avatar name="John" overlay={<Image src={wreathUrl} width="56px" height="56px" />} />
	 */
	overlay?: ReactNode;
	/**
	 * Whether to hide all overlays (both custom and seasonal).
	 * Useful for contexts where overlays would be distracting, like dropdowns or small UI elements.
	 *
	 * @default false
	 */
	hideOverlay?: boolean;
}

export const AvatarSizeMap: Record<AvatarSize, string> = {
	sm: rem(32),
	md: rem(40),
	lg: rem(48),
	xl: rem(56),
	'2xl': rem(72),
};

const useVariantStyle = createUseVariantStyle({
	size: {
		sm: {
			boxSize: AvatarSizeMap.sm,
			fontSize: 'sm',
		},
		md: {
			boxSize: AvatarSizeMap.md,
			fontSize: 'md',
		},
		lg: {
			boxSize: AvatarSizeMap.lg,
			fontSize: 'md',
		},
		xl: {
			boxSize: AvatarSizeMap.xl,
			fontSize: 'lg',
		},
		'2xl': {
			boxSize: AvatarSizeMap['2xl'],
			fontSize: 'xl',
		},
	},
	variant: {
		solid: {},
		outline: {},
	},
});

const Avatar = forwardRef(({ name, overlay, hideOverlay = false, size = 'md', src, variant = 'solid', ...rest }: AvatarProps, ref) => {
	const style = useVariantStyle({ size, variant });
	const { bg, color: textColor } = useNameStyle(name);
	const outlineColor = useColorModeValue('white', 'bg.canvas');

	const seasonalOverlay = useSeasonalAvatarOverlay();

	const hasCustomOverlay = !hideOverlay && overlay != null;
	const hasSeasonalOverlay = !hideOverlay && !hasCustomOverlay && seasonalOverlay.isEnabled && Boolean(seasonalOverlay.overlayImage) && Boolean(src);
	const hasOverlay = hasCustomOverlay || hasSeasonalOverlay;

	const { _notLast, margin, marginInlineEnd, marginInlineStart, m, mx, my, ml, mr, mt, mb, ...innerProps } = rest as any;
	const wrapperProps = { _notLast, margin, marginInlineEnd, marginInlineStart, m, mx, my, ml, mr, mt, mb };

	return (
		<AvatarContextProvider value={{ bg, size, textColor, variant }}>
			{/*
			 * position: relative wrapper — the avatar sets the size, and the
			 * overlay is absolutely positioned on top using inset: 0 + transform: scale().
			 * This makes the overlay always match the avatar's actual rendered
			 * dimensions regardless of how the size was specified (size prop,
			 * custom width/height, or responsive values).
			 */}
			<Box position="relative" display="inline-flex" flexShrink={0} isolation="isolate" data-avatar-wrapper="" data-avatar-size={size} {...wrapperProps}>
				<Box
					alignItems="center"
					as="span"
					role="img"
					aria-label={name}
					bg={variant === 'solid' ? bg : 'white'}
					borderRadius="full"
					overflow="hidden"
					display="inline-flex"
					flexShrink={0}
					justifyContent="center"
					outline="1px solid"
					outlineColor={outlineColor}
					data-testid={`avatar-${name}`}
					ref={ref}
					{...style}
					{...innerProps}
				>
					<AvatarImageWrapper>
						<AvatarImage name={name} src={src} />
					</AvatarImageWrapper>
				</Box>

				{hasOverlay && (
					<Box
						position="absolute"
						inset={0}
						display="flex"
						alignItems="center"
						justifyContent="center"
						pointerEvents="none"
						zIndex={1}
						data-testid={`avatar-overlay-${name}`}
						transform={
							hasSeasonalOverlay
								? `scale(${seasonalOverlay.scale})${seasonalOverlay.translateY ? ` translateY(${seasonalOverlay.translateY})` : ''}`
								: undefined
						}
					>
						{hasCustomOverlay ? (
							overlay
						) : hasSeasonalOverlay && seasonalOverlay.overlayImage ? (
							<Image src={seasonalOverlay.overlayImage} alt="" width="100%" height="100%" objectFit="contain" aria-hidden="true" />
						) : null}
					</Box>
				)}
			</Box>
		</AvatarContextProvider>
	);
});

Avatar.displayName = 'Avatar';

export { Avatar };
